import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useEstateContext } from '@/lib/EstateContext';
import { useSubscription } from '@/hooks/useSubscription';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Loader2, 
  CreditCard, 
  Calendar, 
  AlertTriangle,
  Mail
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { createPageUrl } from '@/utils';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase';

export default function ManageSubscription() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeEstateId, myEstates } = useEstateContext();
  const { subscription: localSub } = useSubscription();
  const queryClient = useQueryClient();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);

  // Fetch full subscription details from Paystack
  const { data: subscriptionData, isLoading } = useQuery({
    queryKey: ['paystack-subscription', activeEstateId],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`/api/paystack-manage?estateId=${activeEstateId}`, {
        headers: { 'Authorization': `Bearer ${session?.access_token}` },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch subscription');
      }
      return response.json();
    },
    enabled: !!activeEstateId && !!localSub?.paystack_subscription_code,
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/paystack-manage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: 'cancel',
          subscriptionCode: localSub.paystack_subscription_code,
          estateId: activeEstateId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel subscription');
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast.success('Subscription cancelled', {
        description: data.message
      });
      queryClient.invalidateQueries(['subscription']);
      queryClient.invalidateQueries(['paystack-subscription']);
      setShowCancelConfirm(false);
    },
    onError: (error) => {
      toast.error('Failed to cancel subscription', {
        description: error.message
      });
    },
  });

  const handleUpdatePaymentMethod = async () => {
    setUpdatingPayment(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/paystack-manage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: 'get_manage_link',
          subscriptionCode: localSub?.paystack_subscription_code,
          email: user?.email,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get manage link');
      }

      // Redirect to Paystack's manage subscription page
      window.location.href = data.link;
    } catch (error) {
      toast.error('Failed to open payment settings', {
        description: error.message
      });
    } finally {
      setUpdatingPayment(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F0F0] dark:bg-gray-900">
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#1883FF' }} />
      </div>
    );
  }

  // Use Paystack API data when available, fall back to local DB data
  const sub = subscriptionData?.subscription;
  const hasPaystackCode = !!localSub?.paystack_subscription_code;
  const isActiveSubscription = ['active', 'past_due'].includes(localSub?.subscription_status);
  const displayTier = subscriptionData?.tier || localSub?.subscription_tier;
  const displayStatus = sub?.status || localSub?.subscription_status;
  const hasDiscount = myEstates.length >= 2;

  const tierNames = {
    small: 'Small Estate',
    medium: 'Medium Estate',
    large: 'Large Estate',
  };

  const tierPrices = {
    small: 'R599',
    medium: 'R1,149',
    large: 'R1,999',
  };

  const tierDiscountedPrices = {
    small: 'R539',
    medium: 'R1,034',
    large: 'R1,799',
  };

  const displayPrice = hasDiscount
    ? (tierDiscountedPrices[displayTier] || '—')
    : (tierPrices[displayTier] || '—');

  return (
    <div className="min-h-screen p-4 md:p-8 bg-[#F0F0F0] dark:bg-gray-900">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate(createPageUrl('Profile'))}
              className="rounded-[24px] md:rounded-[40px] border-gray-300 hover:bg-gray-50 dark:border-white dark:hover:bg-white dark:hover:text-slate-900"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-[#1883FF] hidden sm:block" />
                Manage Subscription
              </h1>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-1">
                View and manage your subscription
              </p>
            </div>
          </div>
        </motion.div>

        {!isActiveSubscription ? (
          <Card className="p-8 text-center bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
            <AlertTriangle className="w-16 h-16 text-[#1883FF] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">No Active Subscription</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              You don't have an active subscription. Subscribe to unlock all features.
            </p>
            <Button
              onClick={() => navigate(createPageUrl('UpgradePage'))}
              className="bg-[#1883FF] hover:bg-[#0066CC] rounded-[24px] md:rounded-[40px]"
            >
              View Plans
            </Button>
          </Card>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
              {/* Current Plan */}
              <div className="mb-6 pb-6 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-sm text-slate-600 dark:text-slate-400 mb-2">Current Plan</h3>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {tierNames[displayTier] || displayTier || 'Active Plan'}
                </h2>
              </div>

              {/* Price */}
              <div className="mb-6 pb-6 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-sm text-slate-600 dark:text-slate-400 mb-2">Price</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-slate-900 dark:text-white">
                    {displayPrice}
                  </span>
                  {hasDiscount && (
                    <span className="text-sm line-through text-slate-400">{tierPrices[displayTier]}</span>
                  )}
                  <span className="text-slate-600 dark:text-slate-400">per month</span>
                </div>
                {hasDiscount && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">10% multi-estate discount applied</p>
                )}
              </div>

              {/* Next Billing Date */}
              <div className="mb-6 pb-6 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-sm text-slate-600 dark:text-slate-400 mb-2">Next Billing Date</h3>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#1883FF]" />
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">
                    {sub?.next_payment_date 
                      ? format(new Date(sub.next_payment_date), 'MMMM d, yyyy')
                      : 'Not available'
                    }
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="mb-6">
                <h3 className="text-sm text-slate-600 dark:text-slate-400 mb-2">Status</h3>
                <Badge className={
                  displayStatus === 'active' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-base px-3 py-1' 
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 text-base px-3 py-1'
                }>
                  {displayStatus === 'active' ? 'Active' : displayStatus}
                </Badge>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3 pt-6 border-t border-slate-200 dark:border-slate-700">
                <div className="flex flex-col sm:flex-row gap-3">
                  {hasPaystackCode && (
                  <Button
                    variant="outline"
                    onClick={handleUpdatePaymentMethod}
                    disabled={updatingPayment}
                    className="flex-1 border-[#1883FF] text-[#1883FF] hover:bg-[#1883FF] hover:text-white dark:border-[#1883FF] dark:hover:bg-[#1883FF] rounded-[24px] md:rounded-[40px]"
                  >
                    {updatingPayment ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    Update Payment Method
                  </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => window.location.href = 'mailto:support@fixmyhood.app?subject=Subscription Support'}
                    className="flex-1 border-gray-300 dark:border-white hover:bg-gray-100 dark:hover:bg-white dark:hover:text-slate-900 rounded-[24px] md:rounded-[40px]"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Support
                  </Button>
                </div>

                {hasPaystackCode && (
                  !showCancelConfirm ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowCancelConfirm(true)}
                    className="w-full sm:w-auto border-red-300 text-red-700 hover:bg-red-100 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-400 dark:hover:text-slate-900 rounded-[24px] md:rounded-[40px]"
                  >
                    Cancel Subscription
                  </Button>
                ) : (
                  <div className="flex-1 space-y-3 p-4 bg-red-50 dark:bg-red-950/30 rounded-lg">
                    <p className="text-sm font-semibold text-red-900 dark:text-red-100">
                      Are you sure? You'll have 7 days grace period.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCancelConfirm(false)}
                        className="flex-1 rounded-[24px] md:rounded-[40px]"
                      >
                        Keep It
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => cancelMutation.mutate()}
                        disabled={cancelMutation.isPending}
                        className="flex-1 bg-red-600 hover:bg-red-700 rounded-[24px] md:rounded-[40px]"
                      >
                        {cancelMutation.isPending && (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        )}
                        Confirm Cancel
                      </Button>
                    </div>
                  </div>
                )
                )}
              </div>

              {/* Email Receipt Note */}
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  📧 Payment receipts are automatically emailed to <strong>{user?.email}</strong> after each successful payment.
                </p>
              </div>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
